import type {
  ComputeRule,
  FieldComparisonCondition,
  FormFieldSchema,
  SchemaRule,
} from '@tasama/types';
import { isValidateRule } from './rule-guards';

// DateInput/DateTimeInput store values as DD/MM/YYYY[ HH:mm] (see format-be-date.util.ts) —
// parse that shape so COMPUTE/VALIDATE can compare dates chronologically, not lexicographically.
const DISPLAY_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})/;

function parseDisplayDate(value: unknown): Date | undefined {
  if (typeof value !== 'string') return undefined;
  const match = value.match(DISPLAY_DATE_PATTERN);
  if (!match) return undefined;
  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? undefined : date;
}

// §5 — FE-only arithmetic/copy over values already on the form.
export function evaluateComputeRule(
  rule: ComputeRule,
  values: Record<string, unknown>
): string | number | undefined {
  if (rule.formula === 'COPY') {
    const sourceField = rule.from[0];
    const source = sourceField ? values[sourceField] : undefined;
    return typeof source === 'string' || typeof source === 'number'
      ? source
      : undefined;
  }

  // DATE_DIFF_DAYS
  const [start, end] = rule.from.map((name) => parseDisplayDate(values[name]));
  if (!start || !end) return undefined;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

// Compares `actual` against either another field's live value (`compareField`) or a literal
// (`value`) — dates compare chronologically, everything else falls back to numeric then string.
export function compareOrdered(
  op: 'gt' | 'lt' | 'gte' | 'lte',
  actual: unknown,
  expected: unknown
): boolean {
  // An ordered comparison needs two answered operands before it means anything. With either
  // side unanswered there is no ordering to test, so it does not match: an empty
  // `check_out_date` is not "on or before" an empty `check_in_date`. Without this guard the
  // string fallback below compared '' against '' and reported every untouched date pair as a
  // violation, which is the phantom message on an untouched Business Trip (TDF-1410).
  // `isFieldFilled` is reused on purpose, so "unanswered" means exactly the same thing here as
  // it does for the isEmpty/isNotEmpty conditions and for the required-ness pass.
  if (!isFieldFilled(actual) || !isFieldFilled(expected)) return false;

  const actualDate = parseDisplayDate(actual);
  const expectedDate = parseDisplayDate(expected);
  let a: number | string;
  let b: number | string;
  if (actualDate && expectedDate) {
    a = actualDate.getTime();
    b = expectedDate.getTime();
  } else if (
    !Number.isNaN(Number(actual)) &&
    !Number.isNaN(Number(expected)) &&
    actual !== '' &&
    expected !== ''
  ) {
    a = Number(actual);
    b = Number(expected);
  } else {
    a = String(actual ?? '');
    b = String(expected ?? '');
  }

  switch (op) {
    case 'gt':
      return a > b;
    case 'lt':
      return a < b;
    case 'gte':
      return a >= b;
    case 'lte':
      return a <= b;
    default:
      return false;
  }
}

// §4 — one `invalidWhen` entry from a VALIDATE rule, evaluated against live form values.
export function fieldComparisonMatches(
  condition: FieldComparisonCondition,
  values: Record<string, unknown>
): boolean {
  const actual = values[condition.field];
  const expected = condition.compareField
    ? values[condition.compareField]
    : condition.value;

  switch (condition.op) {
    case 'eq':
      return actual === expected;
    case 'neq':
      return actual !== expected;
    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte':
      return compareOrdered(condition.op, actual, expected);
    default:
      return false;
  }
}

export function isFieldFilled(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  // A checkbox's "empty" state is the boolean `false` itself, not `undefined`/`''` —
  // without this branch an unchecked box reads as "filled" (false !== undefined/null/'').
  if (typeof value === 'boolean') return value;
  return value !== undefined && value !== null && value !== '';
}

// TDF-1410 FE-2: the message for the first VALIDATE rule that names `fieldName` and is
// currently violated, evaluated straight off live form values.
//
// It has to be evaluated here rather than read out of react-hook-form's `formState.errors`.
// The schema's superRefine already raises the same issue, but RHF only ever writes an error for
// the single field the user just interacted with, and it only does so once that field counts as
// touched. Controller-wired controls (date, select, multiselect) are never handed RHF's `onBlur`,
// so they can never become touched: `formState.isValid` still flips (which is what disables
// SUBMIT) while `errors` stays empty forever. That is the dead SUBMIT button with no reason on it.
export function resolveValidateRuleMessage(
  fieldName: string,
  rules: SchemaRule[],
  values: Record<string, unknown>
): string | undefined {
  const violated = rules
    .filter(isValidateRule)
    .find(
      (rule) =>
        rule.fields.includes(fieldName) &&
        rule.invalidWhen.some((condition) =>
          fieldComparisonMatches(condition, values)
        )
    );
  // `message` is already localized: the BFF resolves the rule's `messageKey` against the CMS
  // using the request's Accept-Language, so it must be rendered verbatim, never re-translated.
  return violated?.message;
}

// Same class of silent failure as resolveValidateRuleMessage, for a field's own SNOW input
// pattern (TDF-1410 BUG-05): a mismatch disables SUBMIT the moment it is typed, but the message
// used to wait on a blur. An empty value is never reported here, because mandatory-ness is a
// separate check that owns that message.
export function resolvePatternErrorMessage(
  field: FormFieldSchema,
  value: unknown,
  fallbackMessage: string
): string | undefined {
  const pattern = field.validation?.pattern;
  if (!pattern) return undefined;
  if (typeof value !== 'string' || value === '') return undefined;
  let re: RegExp;
  try {
    re = new RegExp(pattern);
  } catch {
    // An uncompilable catalogue pattern is ignored, matching buildDynamicFormSchema.
    return undefined;
  }
  return re.test(value)
    ? undefined
    : (field.validation?.message ?? fallbackMessage);
}
